/**
 * GlobalTopBarActions — the fixed function-button cluster rendered on the
 * right side of the global top bar across every page that shows the top bar.
 *
 * Previously this cluster lived only in HomePage (injected via
 * useTopBarActions). It now renders permanently inside GlobalTopBar so the
 * same actions (conversation shortcuts, voice recorder, work timer, create)
 * are available consistently on all top-bar pages. The component is
 * self-contained — no props — and owns its own recording-panel state.
 */

import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, type MenuProps } from "antd";
import {
  PlusOutlined,
  AudioOutlined,
  FolderAddOutlined,
  UserAddOutlined,
  ContactsOutlined,
  CheckSquareOutlined,
  FieldTimeOutlined,
  CalendarOutlined,
  MailOutlined,
  PhoneOutlined,
  UploadOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import ConversationQuickActions from "../common/ConversationQuickActions";
import WorkTimerButton from "../common/WorkTimerButton";
import RecordingPanel from "../home/RecordingPanel";
import NotificationBell from "../notifications/NotificationBell";

export default function GlobalTopBarActions() {
  const navigate = useNavigate();
  const [recordingOpen, setRecordingOpen] = useState(false);

  const handleCreate = useCallback(
    (key: string) => {
      const routes: Record<string, string> = {
        // 实体存在且有顶层创建弹窗
        "new-matter": "/matters?action=create",
        "new-client": "/clients?action=create",
        // 实体存在，但创建入口在父上下文（客户/事项详情页）内
        "new-contact": "/clients",
        "new-task": "/matters",
        "new-email-log": "/matters",
        "new-phone-log": "/matters",
        // 实体存在，落地到对应模块页
        "new-time-entry": "/time-entries",
        "new-event": "/calendar",
        // 其他
        upload: "/workbench",
        more: "/resources",
      };
      const target = routes[key];
      if (target) navigate(target);
    },
    [navigate],
  );

  const createMenuItems: MenuProps["items"] = useMemo(
    () => [
      // 业务实体
      { key: "new-matter", icon: <FolderAddOutlined />, label: "新建事项" },
      { key: "new-client", icon: <UserAddOutlined />, label: "新建客户" },
      { key: "new-contact", icon: <ContactsOutlined />, label: "新建联系人" },
      { key: "new-task", icon: <CheckSquareOutlined />, label: "新建任务" },
      { key: "new-time-entry", icon: <FieldTimeOutlined />, label: "新建工时" },
      { key: "new-event", icon: <CalendarOutlined />, label: "新建日程" },
      { type: "divider" },
      // 沟通记录
      { key: "new-email-log", icon: <MailOutlined />, label: "新建邮件记录" },
      { key: "new-phone-log", icon: <PhoneOutlined />, label: "新建电话记录" },
      { type: "divider" },
      // 其他
      { key: "upload", icon: <UploadOutlined />, label: "上传文档" },
      { type: "divider" },
      { key: "more", icon: <AppstoreOutlined />, label: "更多" },
    ],
    [],
  );

  return (
    <>
      <NotificationBell />
      <ConversationQuickActions />
      <Button
        size="middle"
        type="default"
        icon={<AudioOutlined />}
        aria-label="录音笔"
        onClick={() => setRecordingOpen(true)}
        style={
          recordingOpen
            ? {
                background: "var(--jerry-primary-bg)",
                borderColor: "var(--jerry-primary-500)",
                color: "var(--jerry-primary-700)",
              }
            : undefined
        }
      >
        录音笔
      </Button>
      <WorkTimerButton />
      <Dropdown
        menu={{ items: createMenuItems, onClick: ({ key }) => handleCreate(key) }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <Button type="primary" icon={<PlusOutlined />}>创建</Button>
      </Dropdown>
      <RecordingPanel open={recordingOpen} onClose={() => setRecordingOpen(false)} />
    </>
  );
}
